import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faArrowLeft, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Progress } from '@ui';
import { isEmpty, isPlainObject } from 'lodash';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FieldValues, useFormContext, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';

export interface Step {
    icon?: IconProp;
    title: string;
    description?: string;
    errorKeys?: string[];
    active?: boolean;
    content:
        | Iterable<React.ReactNode>
        | ((nextStep: (force?: boolean) => void, prevStep: () => void) => Iterable<React.ReactNode>)
        | any;
    nextRequirements?: string[];
    renderWhenHidden?: boolean;
    nextCallback?: () => boolean;
    /**
     * Why "Weiter" did nothing, shown after it was pressed and refused.
     *
     * A requirement that is a form field announces itself: the field turns red and
     * `scrollToFirstError` brings it into view. A requirement that is a card somebody
     * has to click has no field and no error to show, so the button simply sat there
     * — pressed, and nothing happened, with no way to find out what was missing.
     */
    nextHint?: React.ReactNode;
}

export interface StepState {
    index: number;
    total: number;
    isFirst: boolean;
    isLast: boolean;
    title?: string;
    description?: string;
}

interface Props {
    steps: Step[] | ((values: FieldValues) => Step[]);
    submitText?: string;
    isLoading?: boolean;
    preventSubmit?: boolean;
    ownControls?: boolean;
    onAbort?: () => void;
    initialStep?: number;
    /** Skips the built-in title/counter/progress block so the host can render its own */
    hideHeader?: boolean;
    /**
     * Scrolls the step's content instead of the page.
     *
     * The header, the progress bar and the controls then stay put while a long step
     * scrolls between them — which is what a wizard inside a bounded panel needs, and
     * wrong for one that simply sits in a document. The host owns the height: this
     * only says "fill it and scroll the middle", so in an auto-height parent the
     * classes below resolve to the old behaviour rather than collapsing to nothing
     * (hence `grow basis-auto`, never `flex-1`).
     */
    scrollContent?: boolean;
    /** Reports the current step so a host outside this tree can label its own controls */
    onStepChange?: (state: StepState) => void;
}

export interface FormStepsRef {
    nextStep: (force?: boolean) => void;
    previousStep: () => void;
    /** Jumps straight to a step — for a host's own stepper, or a shortcut past steps already answered. No validation runs. */
    goToStep: (index: number) => void;
    helpers: Partial<UseFormReturn>;
}

// lodash isEmpty() reports numbers, Dates, Files and every other non-collection as empty, which
// left a filled-in number/date/upload requirement permanently unfulfilled and blocked "Weiter"
// without any visible error — so only strings, arrays and plain objects go through isEmpty()
const hasValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return !Number.isNaN(value);
    if (typeof value === 'string') return value.trim() !== '';
    if (value instanceof Date) return !Number.isNaN(value.getTime());
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return isPlainObject(value) ? !isEmpty(value) : true;
    return true;
};

// Multi-step form wrapper that manages step navigation, validation, and submit triggering
const FormSteps = forwardRef<FormStepsRef, Props>(
    (
        {
            steps: _steps,
            ownControls,
            submitText = 'Speichern',
            preventSubmit,
            onAbort,
            isLoading,
            initialStep = 0,
            hideHeader,
            scrollContent,
            onStepChange
        },
        ref
    ) => {
        const { t } = useTranslation('common');
        const { formState, watch, trigger, setValue, getValues } = useFormContext();
        const watchedFields = watch();
        const [currentStep, setCurrentStep] = useState<number>(initialStep);
        const [blocked, setBlocked] = useState(false);
        // A refusal belongs to the step it happened on.
        useEffect(() => setBlocked(false), [currentStep]);
        const [isInvalid, setIsInvalid] = useState(false);
        const submitButtonRef = useRef<HTMLButtonElement>(undefined);
        // Resolves and filters active steps, re-evaluating when watched fields change
        const steps = useMemo(
            () =>
                (typeof _steps === 'function' ? _steps(watchedFields) : _steps)?.filter(
                    (step) => !('active' in step) || step.active
                ) ?? [],
            [watchedFields, _steps]
        );
        const step = useMemo(() => steps?.[currentStep], [steps, currentStep]);
        const lastStep = currentStep === steps.length - 1;
        const firstStep = currentStep === 0;
        const stepsRef = useRef<HTMLDivElement>(undefined);
        /** The box that scrolls when `scrollContent` is on — see `handleNextStep`. */
        const contentRef = useRef<HTMLDivElement>(undefined);

        // Checks that every nextRequirement has a value. Validity itself comes from trigger();
        // reads getValues() rather than the render snapshot because it runs right after the
        // awaited trigger, where the closure's watch() still holds the pre-validation state
        const nextRequirementsFulfilled = () => {
            if (!step?.nextRequirements) return true;
            return step.nextRequirements.every((requirement) => hasValue(getValues(requirement)));
        };

        // the step content may live in its own scroll container (booking modal), so the error
        // has to be brought into view explicitly — the browser only does that for focused inputs
        const scrollToFirstError = () => {
            requestAnimationFrame(() => {
                const scope = contentRef.current ?? stepsRef.current;
                const target = scope?.querySelector<HTMLElement>('[data-form-error]');
                target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        };

        // Advances to the next step or triggers form submit on the last step
        const handleNextStep = useCallback(
            async (force: boolean = false) => {
                if (!force) {
                    if (step?.nextCallback && !step.nextCallback()) {
                        setBlocked(true);

                        return;
                    }
                    const valid = step?.nextRequirements?.length ? await trigger(step.nextRequirements) : true;
                    if (!valid || !nextRequirementsFulfilled()) {
                        setBlocked(true);
                        scrollToFirstError();
                        return;
                    }
                }
                setBlocked(false);

                // Back to the top of the *step*, not of the document: with an inner
                // scroller the header is outside it, so scrolling that into view would
                // move the page the panel exists to keep still.
                if (scrollContent) contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                else stepsRef.current.scrollIntoView({ behavior: 'smooth' });
                if (currentStep + 1 < steps.length) {
                    setCurrentStep((currentStep) => currentStep + 1);
                } else {
                    setIsInvalid(false);
                    submitButtonRef.current.click();
                }
            },
            [currentStep, steps, step, scrollContent]
        );

        // Goes back one step or calls onAbort if already on the first step
        const handlePreviousStep = () => {
            if (currentStep - 1 >= 0) {
                setCurrentStep(currentStep - 1);
            } else {
                onAbort?.();
            }
        };

        // Jumps to the first step that has a validation error, so the user sees what needs fixing
        useEffect(() => {
            if (isInvalid) return;
            steps.forEach(({ errorKeys }, index) => {
                if (
                    errorKeys?.some((key) =>
                        Object.keys(formState.errors ?? {}).some((errorKey) => errorKey.includes(key))
                    )
                ) {
                    setIsInvalid(true);
                    setCurrentStep(index);
                    scrollToFirstError();
                    return;
                }
            });
        }, [formState, steps, isInvalid]);

        // Keeps a host rendering its own controls in sync — a ref alone would not re-render it
        useEffect(() => {
            onStepChange?.({
                index: currentStep,
                total: steps.length,
                isFirst: firstStep,
                isLast: lastStep,
                title: step?.title,
                description: step?.description
            });
        }, [currentStep, steps.length, firstStep, lastStep, step?.title, step?.description, onStepChange]);

        // Exposes nextStep, previousStep, and setValue helpers to parent components via ref
        useImperativeHandle(ref, () => ({
            nextStep: handleNextStep,
            previousStep: handlePreviousStep,
            goToStep: (index: number) => setCurrentStep(Math.max(0, Math.min(index, steps.length - 1))),
            helpers: { setValue }
        }));

        return (
            <div className={cn('tw-flex tw-flex-1 tw-flex-col tw-justify-between', scrollContent && 'tw-min-h-0')}>
                <div className={cn(scrollContent && 'tw-flex tw-min-h-0 tw-grow tw-basis-auto tw-flex-col')}>
                    {!hideHeader && (
                        <>
                            <div className="tw-flex tw-justify-between" ref={stepsRef}>
                                <span className="tw-inline-block tw-text-lg">{step?.title}</span>
                                <span className="tw-text-md tw-inline-block tw-text-accents-6">
                                    {`${currentStep + 1} / ${steps.length}`}
                                </span>
                            </div>
                            <Progress className="tw-my-2" progress={(currentStep + 1) / steps.length} />
                            {step?.description && (
                                <div className="tw-my-4 tw-rounded-lg tw-bg-accents-1 tw-p-2 tw-text-accents-8">
                                    {step?.description}
                                </div>
                            )}
                        </>
                    )}
                    <div
                        className={cn(
                            'tw-my-6 tw-flex tw-flex-col',
                            // The gutter is reserved so the panel's width does not jump
                            // between a step that scrolls and one that does not.
                            scrollContent &&
                                'tw-min-h-0 tw-grow tw-basis-auto tw-overflow-y-auto tw-pr-3 [scrollbar-gutter:stable]'
                        )}
                        ref={scrollContent ? contentRef : hideHeader ? stepsRef : undefined}
                    >
                        {steps?.map(({ content, renderWhenHidden }, index) => {
                            if (index !== currentStep && renderWhenHidden === false) return null;
                            return (
                                <div
                                    key={`FormSteps_${index}`}
                                    // only the collapsed steps need clipping; on the visible one it
                                    // would cut off whatever the content paints outside its box,
                                    // such as a card's hover shadow
                                    className={index === currentStep ? undefined : 'tw-overflow-hidden'}
                                    style={{
                                        visibility: index === currentStep ? 'visible' : 'hidden',
                                        height: index === currentStep ? 'auto' : 0
                                    }}
                                >
                                    {typeof content === 'function' ? content(handleNextStep, handleNextStep) : content}
                                </div>
                            );
                        })}
                    </div>
                </div>
                {!ownControls && (
                    <div className="tw-my-4 tw-space-y-2">
                        {blocked && step?.nextHint && (
                            <p
                                role="alert"
                                className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-warning"
                            >
                                <FontAwesomeIcon icon={faCircleExclamation} />
                                {step.nextHint}
                            </p>
                        )}
                        <div className="tw-flex tw-gap-2">
                        {(!firstStep || onAbort) && (
                            /* The way back carried no name at all — no text, no title,
                               no aria-label, in every wizard in the app. A tester who
                               needs a visible way back before he dares go forward read
                               it as decoration and walked through a wizard he wanted to
                               correct. The word shows from `sm`; below that the arrow
                               keeps at least an accessible name. */
                            <Button
                                disabled={isLoading}
                                type="button"
                                color="secondary"
                                auto
                                aria-label={t('actions.back')}
                                onPress={handlePreviousStep}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                                <span className="tw-hidden sm:tw-inline">{t('actions.back')}</span>
                            </Button>
                        )}
                        <Button
                            type="button"
                            isLoading={isLoading}
                            isDisabled={preventSubmit}
                            block
                            auto
                            disabled={preventSubmit}
                            onPress={() => handleNextStep()}
                        >
                            {lastStep ? submitText : t('actions.next')}
                        </Button>
                        </div>
                    </div>
                )}
                {/* handleNextStep presses this to submit, so it has to exist even when the
                    host renders the controls itself — otherwise the last step goes nowhere */}
                <button ref={submitButtonRef} className="tw-hidden" hidden type="submit" />
            </div>
        );
    }
);

FormSteps.displayName = 'FormSteps';

export default FormSteps;
